import { useState } from "react";

import { Header } from "@/components/header";
import { RegistryApiError, searchPublicRegistry } from "@/lib/registry-api";
import type { CaseNotice, DomainNotice, SearchType } from "@/lib/types";
import { NoticeDetail } from "@/pages/notice-detail";
import { SearchInterface } from "@/pages/search-interface";

type SearchStatus = "idle" | "loading" | "not-found" | "error";

function App() {
  const [searchType, setSearchType] = useState<SearchType>("case");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNotice, setSelectedNotice] = useState<
    CaseNotice | DomainNotice | null
  >(null);
  const [searchStatus, setSearchStatus] = useState<SearchStatus>("idle");
  const [searchError, setSearchError] = useState<string | null>(null);

  const resetSearchFeedback = () => {
    setSearchStatus("idle");
    setSearchError(null);
  };

  const handleSearch = async () => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery || searchStatus === "loading") return;

    setSearchStatus("loading");
    setSearchError(null);
    setSelectedNotice(null);

    try {
      const result = await searchPublicRegistry(searchType, trimmedQuery);
      if (!result) {
        setSearchStatus("not-found");
        return;
      }
      setSelectedNotice(result);
      setSearchStatus("idle");
    } catch (error) {
      setSearchStatus("error");
      setSearchError(
        error instanceof RegistryApiError
          ? error.message
          : "Registry lookup failed."
      );
    }
  };

  const handleReset = () => {
    setSearchQuery("");
    setSelectedNotice(null);
    resetSearchFeedback();
  };

  const handleSearchTypeChange = (type: SearchType) => {
    setSearchType(type);
    setSearchQuery("");
    setSelectedNotice(null);
    resetSearchFeedback();
  };

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    if (searchStatus !== "loading") {
      resetSearchFeedback();
    }
  };

  const getFeedback = () => {
    if (searchStatus === "not-found") {
      return {
        variant: "warning" as const,
        message: `No registry record was found for "${searchQuery.trim()}".`,
      };
    }
    if (searchStatus === "error") {
      return {
        variant: "error" as const,
        message: searchError ?? "Registry lookup failed.",
      };
    }
    return undefined;
  };

  const feedback = getFeedback();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <h1 className="text-xl sm:text-2xl font-medium">
            NCVIC Removals Registry
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Search for public case numbers or domains to view takedown notices
            and appeal decisions
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {!selectedNotice ? (
          <SearchInterface
            searchType={searchType}
            setSearchType={handleSearchTypeChange}
            searchQuery={searchQuery}
            setSearchQuery={handleSearchQueryChange}
            onSearch={() => {
              void handleSearch();
            }}
            isSearching={searchStatus === "loading"}
            {...(feedback ? { feedback } : {})}
          />
        ) : (
          <NoticeDetail
            notice={selectedNotice}
            searchType={searchType}
            onBack={handleReset}
          />
        )}
      </main>
    </div>
  );
}

export default App;
